import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ConciergeBell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatFlightDay } from '@/lib/disruptions/airport-tz';
import type { CaseRow } from '@/lib/cases-list/types';

interface CaseRowProps {
  row: CaseRow;
}

/**
 * Compose the friendly primary label the operator reads instead of a raw URN:
 *   `LA288 · SCL → GRU · Oct 9 · 184 pax`
 * Each part is dropped when we don't have it, so a sparse case degrades to
 * whatever it can show (e.g. just the flight + date) rather than printing TBD.
 *
 * Issue #605: The date is formatted in the DEPARTURE airport's timezone using
 * formatFlightDay, so it matches the case detail header and shows the correct
 * local date (not the UTC or browser timezone date).
 */
function primaryLabelParts(row: CaseRow, paxLabel: (n: number) => string): string[] {
  const parts: string[] = [];
  if (row.flightNumber) parts.push(row.flightNumber);
  if (row.origin && row.destination) {
    parts.push(`${row.origin} → ${row.destination}`);
  } else if (row.origin) {
    parts.push(row.origin);
  } else if (row.destination) {
    parts.push(row.destination);
  }
  // Issue #605: Format departureAt in the origin airport's timezone.
  // Only show date if we have departureAt - don't fall back to openedAt.
  const date = formatFlightDay(row.departureAt, row.origin);
  if (date && date !== '—') parts.push(date);
  if (typeof row.passengerCount === 'number' && row.passengerCount > 0) {
    parts.push(paxLabel(row.passengerCount));
  }
  return parts;
}

export function CaseRowItem({ row }: CaseRowProps) {
  const { t } = useTranslation('cases');
  const parts = primaryLabelParts(row, (n) => t('units.pax', { n }));
  // Fall back to the airport code (then the URN tail) only if we truly have
  // nothing parseable — the primary label should almost always be populated.
  const headline = parts.length > 0 ? parts.join(' · ') : row.airportCode || row.caseUrn;

  return (
    <Link
      to={`/cases/${row.caseUrn}`}
      className="density-list-row flex items-center justify-between gap-4 p-4 transition-colors hover:bg-muted/50"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold">{headline}</span>
          <Chip>{row.status}</Chip>
          {/* Care-only: this case books hotels/meals/transport but never a
              flight — worth knowing before opening it expecting rebooking. */}
          {row.rebookingDisabled ? (
            <Badge variant="info" size="sm" data-testid="case-row-care-only">
              <ConciergeBell className="h-3 w-3" aria-hidden />
              {t('row.careOnly')}
            </Badge>
          ) : null}
          {row.disruptionReason ? <Chip>{row.disruptionReason}</Chip> : null}
          {row.triggerSource ? (
            <span className="text-xs text-muted-foreground">{row.triggerSource}</span>
          ) : null}
          {row.responsibleOperator ? (
            <span className="text-xs text-muted-foreground">{row.responsibleOperator}</span>
          ) : null}
        </div>
        <p className="mt-1 truncate font-mono text-xs text-muted-foreground" title={row.caseUrn}>
          {row.caseUrn}
        </p>
      </div>
      <dl className="grid grid-cols-3 gap-x-4 text-right text-xs text-muted-foreground">
        <Stat label={t('row.required')} value={row.roomsRequired} />
        <Stat label={t('row.booked')} value={row.roomsBooked} />
        <Stat label={t('row.pending')} value={row.roomsPending} highlight={(row.roomsPending ?? 0) > 0} />
      </dl>
    </Link>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-muted px-1.5 py-0.5 text-xs uppercase text-muted-foreground">
      {children}
    </span>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number | null;
  highlight?: boolean;
}) {
  return (
    <div>
      <dt className="uppercase">{label}</dt>
      <dd className={highlight ? 'font-semibold text-foreground' : 'text-foreground'}>
        {value ?? '—'}
      </dd>
    </div>
  );
}
